import { useCallback, useState } from "react";
import { message } from "antd";
import {
    agreeTransactionScheduleAllocation,
    bulkAgreeSingleSchedules,
    bulkManageSchedules,
    runAllTransactionAllocationChecks,
    runTransactionAllocationCheck,
    unagreeTransactionScheduleAllocation,
} from "../api/transactionScheduleApi";

export default function useTransactionScheduleManageActions({
    selectedRecord,
    refreshDetails,
    loadTransactions,
    refreshReturnsData,
}) {
    const [runningAllChecks, setRunningAllChecks] = useState(false);
    const [runningCheckIds, setRunningCheckIds] = useState([]);

    const [bulkPreviewOpen, setBulkPreviewOpen] = useState(false);
    const [bulkRows, setBulkRows] = useState([]);
    const [bulkPayload, setBulkPayload] = useState({
        institutionId: undefined,
        originalBankAccountId: undefined,
        manualBankAccountId: undefined,
        baseManualFilledAt: null,
        manualFee: 50,
        swiftFee: 100,
        pdfBankOption: "golomt",
    });

    const [lastBulkUndo, setLastBulkUndo] = useState(null);

    const [agreeingAllocationIds, setAgreeingAllocationIds] = useState([]);
    const [unagreeingAllocationIds, setUnagreeingAllocationIds] = useState([]);

    const handleAgreeAllocation = useCallback(
        async (allocationId) => {
            if (!allocationId) return false;

            if (agreeingAllocationIds.includes(allocationId)) {
                return false;
            }

            setAgreeingAllocationIds((prev) => [...prev, allocationId]);

            try {
                await agreeTransactionScheduleAllocation(allocationId);
                message.success("Хуваарилалт зөвшөөрөгдлөө");

                if (selectedRecord?.id) {
                    await refreshDetails(selectedRecord.id);
                }

                await loadTransactions();
                return true;
            } catch (error) {
                message.error(error.message || "Зөвшөөрөх үед алдаа гарлаа");
                return false;
            } finally {
                setAgreeingAllocationIds((prev) =>
                    prev.filter((id) => id !== allocationId),
                );
            }
        },
        [agreeingAllocationIds, loadTransactions, refreshDetails, selectedRecord],
    );

    const handleUnagreeAllocation = useCallback(
        async (allocationId) => {
            if (!allocationId) return false;

            if (unagreeingAllocationIds.includes(allocationId)) {
                return false;
            }

            setUnagreeingAllocationIds((prev) => [...prev, allocationId]);

            try {
                await unagreeTransactionScheduleAllocation(allocationId);
                message.success("Хуваарилалтын зөвшөөрөл цуцлагдлаа");

                if (selectedRecord?.id) {
                    await refreshDetails(selectedRecord.id);
                }

                await loadTransactions();
                return true;
            } catch (error) {
                message.error(error.message || "Цуцлах үед алдаа гарлаа");
                return false;
            } finally {
                setUnagreeingAllocationIds((prev) =>
                    prev.filter((id) => id !== allocationId),
                );
            }
        },
        [loadTransactions, refreshDetails, selectedRecord, unagreeingAllocationIds],
    );

    const handleRunAllocationCheck = useCallback(
        async (allocationId) => {
            setRunningCheckIds((prev) => [...prev, allocationId]);
            try {
                await runTransactionAllocationCheck(allocationId);
                message.success("Шалгалт амжилттай ажиллалаа");
                if (selectedRecord?.id) {
                    await refreshDetails(selectedRecord.id);
                }
                await Promise.all([loadTransactions(), refreshReturnsData()]);
            } catch (error) {
                message.error(error.message || "Шалгалт ажиллуулах үед алдаа гарлаа");
            } finally {
                setRunningCheckIds((prev) => prev.filter((id) => id !== allocationId));
            }
        },
        [loadTransactions, refreshDetails, refreshReturnsData, selectedRecord],
    );

    const handleRunAllChecks = useCallback(async () => {
        setRunningAllChecks(true);
        try {
            await runAllTransactionAllocationChecks();
            message.success("Бүх шалгалт амжилттай ажиллалаа");
            await Promise.all([loadTransactions(), refreshReturnsData()]);
            if (selectedRecord?.id) {
                await refreshDetails(selectedRecord.id);
            }
        } catch (error) {
            message.error(error.message || "Бүх шалгалт ажиллуулах үед алдаа гарлаа");
        } finally {
            setRunningAllChecks(false);
        }
    }, [loadTransactions, refreshDetails, refreshReturnsData, selectedRecord]);

    const openBulkPreview = useCallback((rows) => {
        setBulkRows(rows);
        setBulkPreviewOpen(true);
    }, []);

    const handleBulkManage = useCallback(async (payloadArg) => {
        const ids = bulkRows.map((row) => row.id);
        const payload = payloadArg || bulkPayload;

        try {
            console.log("bulkManage payload", {
                ids,
                institutionId: payload?.institutionId,
                originalBankAccountId: payload?.originalBankAccountId,
                manualBankAccountId: payload?.manualBankAccountId,
                baseManualFilledAt: payload?.baseManualFilledAt,
                manualFee: payload?.manualFee,
                swiftFee: payload?.swiftFee,
                pdfBankOption: payload?.pdfBankOption,
            });

            await bulkManageSchedules({
                ids,
                institutionId: payload.institutionId,
                originalBankAccountId: payload.originalBankAccountId,
                manualBankAccountId: payload.manualBankAccountId,
                baseManualFilledAt:
                    typeof payload.baseManualFilledAt?.format === "function"
                        ? payload.baseManualFilledAt.format("YYYY-MM-DDTHH:mm:ss")
                        : payload.baseManualFilledAt,
                manualFee: payload.manualFee,
                swiftFee: payload.swiftFee,
                pdfBankOption: payload.pdfBankOption || "golomt",
            });

            message.success("Бөөнөөр удирдах үйлдэл амжилттай");
            setLastBulkUndo({
                ids,
                payload,
            });
            setBulkPayload(payload);
            setBulkPreviewOpen(false);
            setBulkRows([]);
            await loadTransactions();
        } catch (error) {
            console.error("bulkManage failed", error);
            message.error(error.message || "Бөөнөөр удирдах үед алдаа гарлаа");
        }
    }, [bulkPayload, bulkRows, loadTransactions]);

    const handleBulkAgree = useCallback(
        async (rows) => {
            try {
                await bulkAgreeSingleSchedules(rows.map((row) => row.id));
                message.success("Бөөнөөр зөвшөөрөх үйлдэл амжилттай");
                await loadTransactions();
            } catch (error) {
                message.error(error.message || "Бөөнөөр зөвшөөрөх үед алдаа гарлаа");
            }
        },
        [loadTransactions],
    );

    return {
        runningAllChecks,
        runningCheckIds,
        bulkPreviewOpen,
        bulkRows,
        bulkPayload,
        lastBulkUndo,
        agreeingAllocationIds,
        unagreeingAllocationIds,
        setBulkPreviewOpen,
        setBulkRows,
        setBulkPayload,
        setLastBulkUndo,
        handleAgreeAllocation,
        handleUnagreeAllocation,
        handleRunAllocationCheck,
        handleRunAllChecks,
        openBulkPreview,
        handleBulkManage,
        handleBulkAgree,
    };
}